import React, { useEffect, useState } from "react";
import { CircleUserRound, ChevronRight, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { RootState, useAppDispatch } from "../../../redux/store";
import { useSelector } from "react-redux";
import { getGeoTrackingList } from "../../../redux/features/geoTracking";
import { fetchEmployees, getSites } from "../../../redux/features/common";
import { setTopNavNameTitle } from "../../../redux/features/mobileLayout";
import { GeoTrackingItem } from "../../../types/model/geoTrackingTypes";
import { DateTimeUtil } from "../../../utils";

const GeoTrackingList: React.FC = () => {
    // Format to YYYY-MM-DD
    const formatDate = (date: Date) => date.toISOString().split("T")[0];

    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const [search, setSearch] = useState<string>("");
    const [filteredEmployees, setFilteredEmployees] = useState<any[]>([]);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
    const dispatch = useAppDispatch();
    const user = useSelector((state: RootState) => state?.user?.user);
    const { employeeList } = useSelector((state: any) => state?.common);

    console.log("employeesList", employeeList);
    const { geoTrackingList } = useSelector((state: RootState) => state?.geoTracking);
    const navigate = useNavigate();
    const employeeOptions = employeeList?.map((emp: any) => ({
        label: `${emp.first_name} ${emp.last_name}`, // Display employee's full name
        value: emp.employee_id, // Use employee_id for the search value
    }));
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearch(value);
        setSelectedEmployeeId(null); // Reset selected employee ID when typing

        // Filter employees based on the search input
        if (value) {
            const filtered = employeeList?.filter((emp: any) =>
                `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredEmployees(filtered || []);
        } else {
            setFilteredEmployees([]);
        }
    };
    const handleEmployeeSelect = (employee: any) => {
        setSearch(`${employee.first_name} ${employee.last_name}`); // Update the search input with selected employee name
        setSelectedEmployeeId(employee.employee_id); // Store the selected employee ID
        setFilteredEmployees([]); // Clear suggestions after selecting
        // Remove the immediate API call here since useEffect will handle it
    };

    const handleSearch = () => {
        dispatch(
            getGeoTrackingList({
                start_date: startDate,
                end_date: endDate,
                employee_id: selectedEmployeeId || "",
            })
        );
    };

    useEffect(() => {
        dispatch(getSites());
    }, [user?.os_emp_account_id]);

    useEffect(() => {
        dispatch(setTopNavNameTitle("Attendance Tracking"));

        // Only call API if we have start and end dates
        if (startDate && endDate) {
            dispatch(
                getGeoTrackingList({
                    start_date: startDate,
                    end_date: endDate,
                    employee_id: selectedEmployeeId || "",
                })
            );
        }
    }, [dispatch, startDate, endDate, selectedEmployeeId]); // Removed user.id as it's not needed

    useEffect(() => {
        dispatch(
            fetchEmployees({
                search: "",
            })
        );
    }, [dispatch]);

    // Initialize dates only once when component mounts
    useEffect(() => {
        if (!startDate && !endDate) {
            const today = new Date();
            const oneMonthAgo = new Date();
            oneMonthAgo.setMonth(today.getMonth() - 1);

            setStartDate(formatDate(oneMonthAgo));
            setEndDate(formatDate(today));
        }
    }, []); // Empty dependency array - runs only once on mount

    // This effect can be removed since we're initializing dates on mount
    // and the date range can be adjusted based on actual data if needed
    return (
        <div className="max-w-full mx-4 border rounded-xl h-[calc(100vh-80px)] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4 p-4">
                <div>
                    <label className="block text-sm font-medium mb-1">Date Start</label>
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full border rounded-md px-3 py-2"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">Date End</label>
                    <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-full border rounded-md px-3 py-2"
                    />
                </div>
            </div>

            {/* <div className="px-4 pb-4">
        <label className="block text-sm font-medium mb-1">Employee</label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
           
          <input
            type="text"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 border rounded-md px-3 py-2"
          />
        </div>
        <button
          className="w-full bg-gray-200 text-black py-2 mt-4 border border-gray-500 rounded-md font-medium"
          onClick={handleSearch}
        >
          Search
        </button>
      </div> */}
            <div className="px-4 pb-4">
                <label className="block text-sm font-medium mb-1">Employee</label>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input
                        type="text"
                        placeholder="Search employees"
                        value={search}
                        onChange={handleSearchChange}
                        className="w-full pl-10 border rounded-md px-3 py-2"
                    />
                </div>
                {filteredEmployees.length > 0 && (
                    <div className="mt-2 max-h-48 overflow-y-auto border p-2 rounded-md">
                        {filteredEmployees.map((employee: any) => (
                            <div
                                key={employee.employee_id}
                                className="cursor-pointer py-2 px-3 hover:bg-gray-200"
                                onClick={() => handleEmployeeSelect(employee)} // Handle employee select
                            >
                                {employee.first_name} {employee.last_name}
                            </div>
                        ))}
                    </div>
                )}
                <button
                    className="w-full bg-gray-200 text-black py-2 mt-4 border border-gray-500 rounded-md font-medium"
                    onClick={handleSearch}
                >
                    Search
                </button>
            </div>
            <hr />

            <div>
                {/* Mobile View */}
                <div className="md:hidden">
                    {Array.isArray(geoTrackingList?.data) &&
                        geoTrackingList?.data.map((item: GeoTrackingItem, index: number) => (
                            <div
                                key={`${item?.tracking_uuid}-${index}`}
                                className={`flex justify-between items-center p-4 ${
                                    index !== geoTrackingList?.data?.length - 1 ? "border-b" : ""
                                }`}
                                onClick={() => {
                                    const state = {
                                        tracking_uuid: item?.tracking_uuid,
                                        tracking_data: item,
                                        os_emp_start_datetime: item?.os_emp_start_datetime,
                                        os_emp_end_datetime: item?.os_emp_end_datetime,
                                        site_name: item?.site?.name,
                                    };
                                    console.log("state", state);
                                    navigate("/m-view-on-map", {
                                        state,
                                    });
                                }}
                            >
                                <div>
                                    <div className="font-semibold">{item?.emp_detail?.emp_name}</div>
                                    <div className="text-sm text-gray-600">{item.os_emp_start_datetime}</div>
                                    <div className="text-sm">{item?.site?.name}</div>
                                </div>
                                <div className="flex flex-col items-end justify-between">
                                    {/* <Link to="/test-view-on-map"> */}
                                    <ChevronRight className="text-xl text-gray-500 cursor-pointer hover:text-gray-700 transition" />
                                    {/* </Link> */}
                                    <div className="text-sm">
                                        {/* {DateTimeUtil.formatDateRangeFancy(item?.shift?.start_time, item?.shift?.end_time)} */}
                                        {/* {item?.shift?.start_time}-{item?.shift?.end_time} */}
                                    </div>
                                </div>
                            </div>
                        ))}
                </div>

                {/* Desktop View */}
                <div className="hidden md:table w-full text-sm border border-gray-300 border-collapse">
                    <div className="table-header-group bg-gray-100 font-medium">
                        <div className="table-row">
                            <div className="table-cell border border-gray-300 px-4 py-2">Employee</div>
                            <div className="table-cell border border-gray-300 px-4 py-2">Date</div>
                            <div className="table-cell border border-gray-300 px-4 py-2">Site</div>
                            <div className="table-cell border border-gray-300 px-4 py-2">Shift</div>
                        </div>
                    </div>
                    <div className="table-row-group">
                        {Array.isArray(geoTrackingList?.data) &&
                            geoTrackingList?.data.map((item: any, index: number) => (
                                <div
                                    key={item?.tracking_uuid}
                                    className="table-row"
                                    onClick={() => {
                                        const state = {
                                            tracking_uuid: item?.tracking_uuid,
                                            tracking_data: item,
                                            os_emp_start_datetime: item?.os_emp_start_datetime,
                                            os_emp_end_datetime: item?.os_emp_end_datetime,
                                            site_name: item?.site?.name,
                                        };
                                        console.log("state", state);
                                        navigate("/m-view-on-map", {
                                            state,
                                        });
                                    }}
                                >
                                    <div className="table-cell border border-gray-300 px-4 py-2">
                                        <div className="flex items-center gap-2">
                                            <CircleUserRound className="text-gray-500" />
                                            {item?.emp_detail?.emp_name}
                                        </div>
                                    </div>
                                    <div className="table-cell border border-gray-300 px-4 py-2">
                                        {item.os_emp_start_datetime}
                                    </div>
                                    <div className="table-cell border border-gray-300 px-4 py-2">
                                        {item?.site?.name}
                                    </div>
                                    <div className="table-cell border border-gray-300 px-4 py-2">
                                        {item?.shift?.start_time}-{item?.shift?.end_time}
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GeoTrackingList;
